import { useState } from 'react';
import { api } from '../api.js';
import { Modal } from '../components/Modal.js';
import { Badge, ConceptName, Empty } from '../components/ui.js';
import { eur, inputToCents } from '../format.js';
import { useApp } from '../state.js';

export function ReceivablesPage() {
  const { balance, catalog, movements, run } = useApp();
  const [open, setOpen] = useState<string | null>(null);
  if (!balance || !catalog || !movements) return <div className="skeleton" />;
  const list = balance.receivables;
  const r = list.find((x) => x.receivableId === open);
  return (
    <div className="page">
      <div className="notice">
        <div>
          <strong>Lo pendiente se cierra a los {catalog.household.settings.receivableDueDays} días</strong>
          <div className="muted">Vence a las 00:00 del décimo día desde el pago (fecha editable por cuenta). Lo no recuperado vuelve como gasto al período original; un Bizum posterior al cierre entra como ingreso independiente.</div>
        </div>
        <Badge>Activación manual</Badge>
      </div>
      <div className="panel">
        <h2>Cuentas corrientes</h2>
        {list.length === 0 ? <Empty>No hay devoluciones registradas.</Empty> : null}
        {list.map((x) => {
          const concept = catalog.concepts.find((c) => c.id === x.conceptId);
          return (
            <div className="row" key={x.receivableId}>
              <div>
                <strong>{x.description.split(' · ')[0]}</strong> {concept ? <ConceptName id={concept.id} name={concept.name} /> : null}
                <div className="muted">
                  {x.period} · pago {eur(x.grossCents)} · esperado {eur(x.expectedCents)} · recibido {eur(x.receivedCents)} · vence {x.dueOn}
                </div>
                {x.closedOn ? <div className="muted">Cerrada el {x.closedOn} · residual {eur(x.residualCents ?? 0)} vuelto al costo propio</div> : null}
              </div>
              <div className="inline">
                <Badge kind={x.closedOn ? 'neutral' : x.overdue ? 'red' : 'warn'}>{x.closedOn ? 'Cerrada' : x.overdue ? 'Vencida' : 'Pendiente'}</Badge>
                <span className="big">{eur(x.pendingCents)}</span>
                <button type="button" className="button small" onClick={() => setOpen(x.receivableId)}>
                  Abrir
                </button>
              </div>
            </div>
          );
        })}
      </div>
      {r ? <ReceivableModal r={r} onClose={() => setOpen(null)} /> : null}
    </div>
  );
}

function ReceivableModal({ r, onClose }: { r: NonNullable<ReturnType<typeof useApp>['balance']>['receivables'][number]; onClose: () => void }) {
  const { run, movements, balance } = useApp();
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(balance?.today ?? '');
  const [txId, setTxId] = useState('');
  const [due, setDue] = useState(r.dueOn);
  const [error, setError] = useState<string | null>(null);
  const receipts = (movements?.receipts ?? []).filter((x) => x.receivableId === r.receivableId);
  const candidates = (movements?.transactions ?? []).filter((t) => t.direction === 'in' && !movements?.receipts.some((x) => x.transactionId === t.id) && !movements?.allocations.some((a) => a.transactionId === t.id));
  async function pay() {
    const cents = inputToCents(amount);
    if (cents === null || cents <= 0 || cents > r.pendingCents) return setError('El importe debe ser positivo y no superar lo pendiente.');
    const ok = await run(() => api.addReceipt(r.receivableId, { amountCents: cents, receivedOn: date, transactionId: txId || null }), 'Devolución aplicada sin duplicar ingreso');
    if (ok) onClose();
  }
  return (
    <Modal
      title={`Cuenta corriente · ${r.description}`}
      onClose={onClose}
      error={error}
      actions={
        r.closedOn ? null : (
          <button type="button" className="button primary" onClick={() => void pay()}>
            Registrar devolución
          </button>
        )
      }
    >
      <dl className="kv">
        <dt>Pago original</dt>
        <dd>{eur(r.grossCents)}</dd>
        <dt>Importe esperado</dt>
        <dd>{eur(r.expectedCents)}</dd>
        <dt>Recuperado</dt>
        <dd>{eur(r.receivedCents)}</dd>
        <dt>Pendiente</dt>
        <dd>{eur(r.pendingCents)}</dd>
        <dt>Costo propio hoy</dt>
        <dd>{eur(r.closedOn ? r.grossCents - r.receivedCents : r.grossCents - r.expectedCents)}</dd>
      </dl>
      {receipts.length ? (
        <>
          <h3 style={{ marginTop: 16 }}>Cobros</h3>
          {receipts.map((x) => (
            <div className="row" key={x.id}>
              <div>
                {x.receivedOn}
                {x.note ? <span className="muted"> · {x.note}</span> : null}
              </div>
              <span className="number">{eur(x.amountCents)}</span>
            </div>
          ))}
        </>
      ) : null}
      {!r.closedOn ? (
        <>
          <div className="field-row" style={{ marginTop: 16 }}>
            <label>
              Devolución recibida (€)
              <input data-autofocus inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
            </label>
            <label>
              Fecha
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </label>
          </div>
          <label>
            Vincular a una entrada registrada (Bizum recibido) — opcional
            <select value={txId} onChange={(e) => setTxId(e.target.value)}>
              <option value="">Sin vincular</option>
              {candidates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.date} · {t.description} · {eur(t.amountCents)}
                </option>
              ))}
            </select>
          </label>
          <div className="field-row">
            <label>
              Vencimiento
              <input type="date" value={due} onChange={(e) => setDue(e.target.value)} />
            </label>
            <div style={{ alignSelf: 'end', paddingBottom: 14 }}>
              <button type="button" className="button" onClick={() => void run(() => api.updateReceivable(r.receivableId, due), 'Vencimiento actualizado')}>
                Guardar vencimiento
              </button>
            </div>
          </div>
          <button type="button" className="button danger" onClick={() => void run(() => api.closeReceivable(r.receivableId), `Pendiente cerrado: ${eur(r.pendingCents)} vuelve al gasto original`).then((ok) => ok && onClose())}>
            Cerrar pendiente y asumir residual
          </button>
        </>
      ) : (
        <p className="muted">Cuenta cerrada el {r.closedOn}. Un cobro posterior se registra como entrada independiente en Movimientos.</p>
      )}
    </Modal>
  );
}
